import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Users, Building2, Landmark, BarChart3, Download, Filter,
  ChevronLeft, MoreVertical, Trash2, Check, Clock, X, AlertCircle,
  TrendingUp, MapPin, Briefcase, Search, Settings, Calendar, Save,
  Camera, Upload, Play, ArrowUp, ArrowDown, Loader2, Image as ImageIcon, Pencil
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ImageUpload } from "../components/ImageUpload";

const API = `/api/win2026`;
const UPLOADS_URL = `/api/uploads`;

// Logos
const LOGOS = {
  win: `${UPLOADS_URL}/win-logo.png`,
  sag: `${UPLOADS_URL}/sag-logo.png`,
  tdc: `${UPLOADS_URL}/tdc-logo.png`
};

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-700",
  follow_up: "bg-blue-100 text-blue-700"
};

const statusIcons = {
  pending: Clock,
  approved: Check,
  declined: X,
  follow_up: AlertCircle
};

export default function Win2026Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [exhibitors, setExhibitors] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [eventSettings, setEventSettings] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check for stored token
  useEffect(() => {
    const storedToken = localStorage.getItem("win2026_token");
    if (storedToken) {
      setAuthToken(storedToken);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      setAuthToken(res.data.token);
      localStorage.setItem("win2026_token", res.data.token);
      setIsAuthenticated(true);
      toast.success("Login successful!");
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthToken("");
    localStorage.removeItem("win2026_token");
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, entrepreneursRes, exhibitorsRes, institutionsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/entrepreneurs`),
        axios.get(`${API}/admin/exhibitors`),
        axios.get(`${API}/admin/institutions`),
        axios.get(`${API}/settings`)
      ]);
      setAnalytics(analyticsRes.data);
      setEntrepreneurs(entrepreneursRes.data);
      setExhibitors(exhibitorsRes.data);
      setInstitutions(institutionsRes.data);
      setEventSettings(settingsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (type, id, status) => {
    try {
      await axios.put(`${API}/admin/${type}/${id}/status`, { status });
      toast.success("Status updated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteRegistration = async (type, id) => {
    try {
      await axios.delete(`${API}/admin/${type}/${id}`);
      toast.success("Registration deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete registration");
    }
  };

  const exportCSV = (type) => {
    window.open(`${API}/admin/export/${type}`, "_blank");
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#008B9B] to-[#006064] flex items-center justify-center p-4">
        <motion.div 
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="text-center mb-8">
            <img src={LOGOS.win} alt="W.I.N Summit" className="h-24 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-500 text-sm mt-2">W.I.N Entrepreneurial Summit 2026</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input 
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full border-gray-300 text-gray-900 placeholder:text-gray-400 bg-white"
                data-testid="admin-password"
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#008B9B] hover:bg-[#006064]"
              data-testid="admin-login-btn"
            >
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <Link to="/win2026" className="text-[#008B9B] hover:underline text-sm">
              ← Back to W.I.N Summit
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-gray-400 hover:text-gray-600">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <img src={LOGOS.win} alt="W.I.N" className="h-10" />
            <div>
              <h1 className="font-bold text-gray-900">W.I.N 2026 Admin</h1>
              <p className="text-xs text-gray-500">Registration Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={fetchAllData} disabled={loading}>
              Refresh
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-gray-500">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 bg-white border">
            <TabsTrigger value="overview" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="w-4 h-4" />
              Event Settings
            </TabsTrigger>
            <TabsTrigger value="entrepreneurs" className="gap-2">
              <Users className="w-4 h-4" />
              Entrepreneurs ({entrepreneurs.length})
            </TabsTrigger>
            <TabsTrigger value="exhibitors" className="gap-2">
              <Building2 className="w-4 h-4" />
              Exhibitors ({exhibitors.length})
            </TabsTrigger>
            <TabsTrigger value="institutions" className="gap-2">
              <Landmark className="w-4 h-4" />
              Institutions ({institutions.length})
            </TabsTrigger>
            <TabsTrigger value="gallery" className="gap-2" data-testid="gallery-tab">
              <Camera className="w-4 h-4" />
              Gallery
            </TabsTrigger>
          </TabsList>

          {/* Event Settings Tab */}
          <TabsContent value="settings">
            <EventSettingsForm settings={eventSettings} onUpdate={fetchAllData} />
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <GalleryManager />
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {analytics && (
              <div className="space-y-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <StatCard 
                    title="Total Registrations"
                    value={analytics.totals.total}
                    icon={TrendingUp}
                    color="text-[#008B9B]"
                    bgColor="bg-[#E0F7FA]"
                  />
                  <StatCard 
                    title="Entrepreneurs"
                    value={analytics.totals.entrepreneurs}
                    icon={Users}
                    color="text-[#008B9B]"
                    bgColor="bg-[#E0F7FA]"
                  />
                  <StatCard 
                    title="Exhibitors"
                    value={analytics.totals.exhibitors}
                    icon={Building2}
                    color="text-[#D4AF37]"
                    bgColor="bg-amber-50"
                  />
                  <StatCard 
                    title="Institutions"
                    value={analytics.totals.institutions}
                    icon={Landmark}
                    color="text-[#006064]"
                    bgColor="bg-teal-50"
                  />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* By Island */}
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#008B9B]" />
                      Entrepreneurs by Island
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(analytics.entrepreneurs_by_island || {}).slice(0, 8).map(([island, count]) => (
                        <div key={island} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{island}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-gray-100 rounded-full h-2">
                              <div 
                                className="bg-[#008B9B] rounded-full h-2" 
                                style={{ width: `${(count / analytics.totals.entrepreneurs) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-8 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* By Stage */}
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-[#D4AF37]" />
                      By Business Stage
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(analytics.entrepreneurs_by_stage || {}).map(([stage, count]) => (
                        <div key={stage} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium text-gray-700">{stage}</span>
                          <span className="text-2xl font-bold text-[#008B9B]">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top Needs */}
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4">Top Needs</h3>
                    <div className="space-y-2">
                      {Object.entries(analytics.top_needs || {}).slice(0, 8).map(([need, count]) => (
                        <div key={need} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 truncate pr-2">{need}</span>
                          <span className="font-medium text-[#008B9B]">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Meeting Requests */}
                <div className="bg-gradient-to-r from-[#008B9B] to-[#006064] rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Summit Meeting Requests</h3>
                      <p className="text-white/70 text-sm">Entrepreneurs who want to meet with SAG at the Summit</p>
                    </div>
                    <div className="text-5xl font-bold">{analytics.meeting_requests}</div>
                  </div>
                </div>

                {/* Export Buttons */}
                <div className="flex flex-wrap gap-4">
                  <Button onClick={() => exportCSV("entrepreneurs")} className="gap-2 bg-[#008B9B] hover:bg-[#006064]">
                    <Download className="w-4 h-4" />
                    Export Entrepreneurs CSV
                  </Button>
                  <Button onClick={() => exportCSV("exhibitors")} className="gap-2 bg-[#D4AF37] hover:bg-[#B4941F]">
                    <Download className="w-4 h-4" />
                    Export Exhibitors CSV
                  </Button>
                  <Button onClick={() => exportCSV("institutions")} className="gap-2 bg-[#006064] hover:bg-[#004D4D]">
                    <Download className="w-4 h-4" />
                    Export Institutions CSV
                  </Button>
                  <Button onClick={() => exportCSV("all")} variant="outline" className="gap-2">
                    <Download className="w-4 h-4" />
                    Export All Combined
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Entrepreneurs Tab */}
          <TabsContent value="entrepreneurs">
            <RegistrationTable 
              data={entrepreneurs}
              type="entrepreneurs"
              columns={[
                { key: "full_name", label: "Name" },
                { key: "business_name", label: "Business" },
                { key: "email", label: "Email" },
                { key: "island", label: "Island" },
                { key: "business_stage", label: "Stage" },
                { key: "industry_category", label: "Industry" },
                { key: "status", label: "Status" },
              ]}
              onStatusChange={updateStatus}
              onDelete={deleteRegistration}
            />
          </TabsContent>

          {/* Exhibitors Tab */}
          <TabsContent value="exhibitors">
            <RegistrationTable 
              data={exhibitors}
              type="exhibitors"
              columns={[
                { key: "company_name", label: "Company" },
                { key: "rep_name", label: "Representative" },
                { key: "email", label: "Email" },
                { key: "organization_type", label: "Org Type" },
                { key: "status", label: "Status" },
              ]}
              onStatusChange={updateStatus}
              onDelete={deleteRegistration}
            />
          </TabsContent>

          {/* Institutions Tab */}
          <TabsContent value="institutions">
            <RegistrationTable 
              data={institutions}
              type="institutions"
              columns={[
                { key: "institution_name", label: "Institution" },
                { key: "rep_name", label: "Representative" },
                { key: "email", label: "Email" },
                { key: "institution_type", label: "Type" },
                { key: "want_booth", label: "Wants Booth" },
                { key: "status", label: "Status" },
              ]}
              onStatusChange={updateStatus}
              onDelete={deleteRegistration}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color, bgColor }) {
  return (
    <motion.div 
      className="bg-white rounded-xl p-6 shadow-sm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl ${bgColor} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </motion.div>
  );
}

function RegistrationTable({ data, type, columns, onStatusChange, onDelete }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const filteredData = data.filter(item => {
    const matchesSearch = Object.values(item).some(val => 
      String(val).toLowerCase().includes(search.toLowerCase())
    );
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric"
    });
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-xl shadow-sm">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
            <SelectItem value="follow_up">Follow Up</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-gray-500 self-center">{filteredData.length} records</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                {columns.map(col => (
                  <th key={col.key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {col.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 2} className="px-4 py-12 text-center text-gray-500">
                    No registrations found
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    {columns.map(col => (
                      <td key={col.key} className="px-4 py-4 text-sm text-gray-900">
                        {col.key === "status" ? (
                          <StatusBadge status={item.status} />
                        ) : col.key === "want_booth" || col.key === "want_meetings" ? (
                          item[col.key] ? <Check className="w-4 h-4 text-green-600" /> : <X className="w-4 h-4 text-gray-300" />
                        ) : col.key === "needs" || col.key === "services_offered" || col.key === "support_offered" ? (
                          <span className="text-xs">{(item[col.key] || []).slice(0, 2).join(", ")}{item[col.key]?.length > 2 ? "..." : ""}</span>
                        ) : (
                          item[col.key] || "-"
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onStatusChange(type, item.id, "approved")} className="text-green-600">
                            <Check className="w-4 h-4 mr-2" /> Approve
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onStatusChange(type, item.id, "follow_up")} className="text-blue-600">
                            <AlertCircle className="w-4 h-4 mr-2" /> Follow Up
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onStatusChange(type, item.id, "declined")} className="text-red-600">
                            <X className="w-4 h-4 mr-2" /> Decline
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteConfirm(item)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Registration</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this registration? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button 
              variant="destructive"
              onClick={() => {
                onDelete(type, deleteConfirm.id);
                setDeleteConfirm(null);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const Icon = statusIcons[status] || Clock;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || statusColors.pending}`}>
      <Icon className="w-3 h-3" />
      {status?.replace("_", " ")}
    </span>
  );
}

// ===================== GALLERY MANAGER =====================
function GalleryManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editCaption, setEditCaption] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const fileInputRef = useRef(null);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setItems(res.data);
    } catch (err) {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGallery(); }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let added = 0;
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const uploadRes = await axios.post(`/api/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const isVideo = file.type.startsWith("video/");
        await axios.post(`${API}/admin/gallery`, {
          type: isVideo ? "video" : "photo",
          url: uploadRes.data.url,
          caption: file.name.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " "),
        });
        added++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (added > 0) {
      toast.success(`${added} item${added > 1 ? "s" : ""} added to gallery`);
      fetchGallery();
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/gallery/${id}`);
      toast.success("Item removed from gallery");
      setItems(prev => prev.filter(i => i.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      toast.error("Failed to delete item");
    }
  };

  const handleSaveCaption = async (id) => {
    try {
      await axios.put(`${API}/admin/gallery/${id}`, { caption: editCaption });
      setItems(prev => prev.map(i => i.id === id ? { ...i, caption: editCaption } : i));
      setEditingId(null);
      toast.success("Caption updated");
    } catch (err) {
      toast.error("Failed to update caption");
    }
  };

  const handleToggleFeatured = async (id, current) => {
    try {
      await axios.put(`${API}/admin/gallery/${id}`, { featured: !current });
      setItems(prev => prev.map(i => i.id === id ? { ...i, featured: !current } : i));
      toast.success(!current ? "Set as featured" : "Removed from featured");
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const handleMove = async (index, direction) => {
    const newItems = [...items];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newItems.length) return;
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    const reordered = newItems.map((item, i) => ({ ...item, order: i }));
    setItems(reordered);
    try {
      await axios.put(`${API}/admin/gallery-reorder`, {
        items: reordered.map(i => ({ id: i.id, order: i.order }))
      });
    } catch (err) {
      toast.error("Failed to reorder");
      fetchGallery();
    }
  };

  const photos = items.filter(i => i.type === "photo");
  const videos = items.filter(i => i.type === "video");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#008B9B]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Upload */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#008B9B]/10 flex items-center justify-center">
              <Camera className="w-6 h-6 text-[#008B9B]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Gallery Manager</h2>
              <p className="text-sm text-gray-500">{photos.length} photos, {videos.length} videos</p>
            </div>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/mov"
              multiple
              onChange={handleUpload}
              className="hidden"
              data-testid="gallery-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-[#008B9B] hover:bg-[#006064] gap-2"
              data-testid="gallery-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Uploading..." : "Upload Photos / Videos"}
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-400">Supports JPG, PNG, WEBP, GIF (images) and MP4 (videos). Max 50MB per file. Changes appear instantly on the public W.I.N. page and homepage.</p>
      </div>

      {/* Photos Grid */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-[#008B9B]" />
          Photos ({photos.length})
        </h3>
        {photos.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No photos yet. Upload some to get started.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((item, index) => (
              <div key={item.id} className="relative group rounded-lg overflow-hidden border border-gray-200" data-testid={`gallery-photo-${index}`}>
                <img src={item.url} alt={item.caption} className="w-full h-36 object-cover" />
                {item.featured && (
                  <span className="absolute top-2 left-2 bg-[#D4AF37] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">FEATURED</span>
                )}
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  <button onClick={() => handleMove(items.indexOf(item), -1)} className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gray-100" title="Move up">
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleMove(items.indexOf(item), 1)} className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gray-100" title="Move down">
                    <ArrowDown className="w-4 h-4" />
                  </button>
                  <button onClick={() => { setEditingId(item.id); setEditCaption(item.caption); }} className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gray-100" title="Edit caption">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleToggleFeatured(item.id, item.featured)} className="w-8 h-8 bg-[#D4AF37] text-white rounded-full flex items-center justify-center hover:bg-[#B4941F]" title="Toggle featured">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => setDeleteConfirm(item.id)} className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {/* Caption */}
                <div className="p-2 bg-white">
                  {editingId === item.id ? (
                    <div className="flex gap-1">
                      <Input
                        value={editCaption}
                        onChange={(e) => setEditCaption(e.target.value)}
                        className="h-7 text-xs border-gray-300 text-gray-900 bg-white"
                        onKeyDown={(e) => e.key === "Enter" && handleSaveCaption(item.id)}
                        autoFocus
                      />
                      <Button size="sm" onClick={() => handleSaveCaption(item.id)} className="h-7 px-2 bg-[#008B9B]">
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-7 px-2">
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 truncate">{item.caption || "No caption"}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Videos Grid */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Play className="w-4 h-4 text-[#008B9B]" />
          Videos ({videos.length})
        </h3>
        {videos.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No videos yet. Upload MP4 files to add.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((item, index) => (
              <div key={item.id} className="relative rounded-lg overflow-hidden border border-gray-200" data-testid={`gallery-video-${index}`}>
                <video className="w-full h-44 object-cover bg-black" controls preload="metadata" playsInline>
                  <source src={item.url} type="video/mp4" />
                </video>
                <div className="p-3 bg-white flex items-center justify-between">
                  {editingId === item.id ? (
                    <div className="flex gap-1 flex-1">
                      <Input
                        value={editCaption}
                        onChange={(e) => setEditCaption(e.target.value)}
                        className="h-7 text-xs border-gray-300 text-gray-900 bg-white"
                        onKeyDown={(e) => e.key === "Enter" && handleSaveCaption(item.id)}
                        autoFocus
                      />
                      <Button size="sm" onClick={() => handleSaveCaption(item.id)} className="h-7 px-2 bg-[#008B9B]">
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-7 px-2">
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700 font-medium truncate flex-1">{item.caption || "No caption"}</p>
                  )}
                  <div className="flex gap-1 ml-2">
                    <button onClick={() => handleMove(items.indexOf(item), -1)} className="w-7 h-7 border rounded flex items-center justify-center hover:bg-gray-50" title="Move up">
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button onClick={() => handleMove(items.indexOf(item), 1)} className="w-7 h-7 border rounded flex items-center justify-center hover:bg-gray-50" title="Move down">
                      <ArrowDown className="w-3 h-3" />
                    </button>
                    <button onClick={() => { setEditingId(item.id); setEditCaption(item.caption); }} className="w-7 h-7 border rounded flex items-center justify-center hover:bg-gray-50" title="Edit">
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button onClick={() => setDeleteConfirm(item.id)} className="w-7 h-7 border border-red-200 rounded flex items-center justify-center hover:bg-red-50 text-red-500" title="Delete">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Delete Gallery Item</DialogTitle>
            <DialogDescription className="text-gray-500">
              Are you sure you want to remove this item from the gallery? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} data-testid="gallery-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== EVENT SETTINGS FORM =====================
function EventSettingsForm({ settings, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    event_name: "",
    tagline: "",
    description: "",
    date_start: "",
    date_end: "",
    date_display: "",
    location: "",
    venue: "",
    host_name: "",
    partner_name: "",
    logo_url: "",
    registration_open: true,
    meeting_days: ""
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        event_name: settings.event_name || "",
        tagline: settings.tagline || "",
        description: settings.description || "",
        date_start: settings.date_start || "",
        date_end: settings.date_end || "",
        date_display: settings.date_display || "",
        location: settings.location || "",
        venue: settings.venue || "",
        host_name: settings.host_name || "",
        partner_name: settings.partner_name || "",
        logo_url: settings.logo_url || "",
        registration_open: settings.registration_open ?? true,
        meeting_days: (settings.meeting_days || []).join(", ")
      });
    }
  }, [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        meeting_days: formData.meeting_days.split(",").map(d => d.trim()).filter(d => d)
      };
      await axios.put(`${API}/admin/settings`, submitData);
      toast.success("Event settings updated successfully!");
      onUpdate();
    } catch (error) {
      console.error("Failed to update settings:", error);
      toast.error("Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-[#008B9B]/10 flex items-center justify-center">
          <Settings className="w-6 h-6 text-[#008B9B]" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Event Settings</h2>
          <p className="text-sm text-gray-500">Manage W.I.N Summit 2026 event details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-gray-700 font-semibold">Event Name</Label>
            <Input 
              value={formData.event_name}
              onChange={(e) => setFormData({...formData, event_name: e.target.value})}
              placeholder="W.I.N Entrepreneurial Summit 2026"
              className="mt-1 border-gray-300 text-gray-900 placeholder:text-gray-400 bg-white"
            />
          </div>
          <div>
            <Label className="text-gray-700 font-semibold">Tagline</Label>
            <Input 
              value={formData.tagline}
              onChange={(e) => setFormData({...formData, tagline: e.target.value})}
              placeholder="From Idea to Activation"
              className="mt-1 border-gray-300 text-gray-900 placeholder:text-gray-400 bg-white"
            />
          </div>
        </div>

        <div>
          <Label className="text-gray-700 font-semibold">Description</Label>
          <Textarea 
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Event description..."
            className="mt-1 border-gray-300 text-gray-900 placeholder:text-gray-400 bg-white"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Label className="text-gray-700 font-semibold">Start Date</Label>
            <Input 
              type="date"
              value={formData.date_start}
              onChange={(e) => setFormData({...formData, date_start: e.target.value})}
              className="mt-1 border-gray-300 text-gray-900 bg-white"
            />
          </div>
          <div>
            <Label className="text-gray-700 font-semibold">End Date</Label>
            <Input 
              type="date"
              value={formData.date_end}
              onChange={(e) => setFormData({...formData, date_end: e.target.value})}
              className="mt-1 border-gray-300 text-gray-900 bg-white"
            />
          </div>
          <div>
            <Label className="text-gray-700 font-semibold">Display Date (shown to users)</Label>
            <Input 
              value={formData.date_display}
              onChange={(e) => setFormData({...formData, date_display: e.target.value})}
              placeholder="April 9, 2026"
              className="mt-1 border-gray-300 text-gray-900 placeholder:text-gray-400 bg-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-gray-700 font-semibold">Location</Label>
            <Input 
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              placeholder="The Bahamas"
              className="mt-1 border-gray-300 text-gray-900 placeholder:text-gray-400 bg-white"
            />
          </div>
          <div>
            <Label className="text-gray-700 font-semibold">Venue</Label>
            <Input 
              value={formData.venue}
              onChange={(e) => setFormData({...formData, venue: e.target.value})}
              placeholder="TBD"
              className="mt-1 border-gray-300 text-gray-900 placeholder:text-gray-400 bg-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-gray-700 font-semibold">Host Organization</Label>
            <Input 
              value={formData.host_name}
              onChange={(e) => setFormData({...formData, host_name: e.target.value})}
              placeholder="Tourism Development Corporation"
              className="mt-1 border-gray-300 text-gray-900 placeholder:text-gray-400 bg-white"
            />
          </div>
          <div>
            <Label className="text-gray-700 font-semibold">Partner Organization</Label>
            <Input 
              value={formData.partner_name}
              onChange={(e) => setFormData({...formData, partner_name: e.target.value})}
              placeholder="Sebastian Alliance Group"
              className="mt-1 border-gray-300 text-gray-900 placeholder:text-gray-400 bg-white"
            />
          </div>
        </div>

        <div>
          <Label className="text-gray-700 font-semibold">Meeting Days (comma separated)</Label>
          <Input 
            value={formData.meeting_days}
            onChange={(e) => setFormData({...formData, meeting_days: e.target.value})}
            placeholder="April 9, 2026, April 10, 2026, April 11, 2026"
            className="mt-1 border-gray-300 text-gray-900 placeholder:text-gray-400 bg-white"
          />
          <p className="text-xs text-gray-500 mt-1">These will appear in the entrepreneur registration form for meeting scheduling</p>
        </div>

        <div>
          <ImageUpload
            label="Event Logo (optional)"
            value={formData.logo_url}
            onChange={(url) => setFormData({...formData, logo_url: url})}
          />
        </div>

        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
          <Switch 
            checked={formData.registration_open}
            onCheckedChange={(checked) => setFormData({...formData, registration_open: checked})}
          />
          <div>
            <Label>Registration Open</Label>
            <p className="text-xs text-gray-500">Allow new registrations for the event</p>
          </div>
        </div>

        <div className="flex justify-end gap-4 pt-4 border-t">
          <Button 
            type="submit" 
            disabled={loading}
            className="bg-[#008B9B] hover:bg-[#006064] gap-2"
          >
            <Save className="w-4 h-4" />
            {loading ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </form>
    </div>
  );
}
